import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Collapse, Descriptions, Form, Input, InputNumber, Radio, Row, Select, Space, Spin, Switch, Tag, Upload, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createProducerRun, getProducersRegistry, optimizeIdeaToVideo, searchContent, uploadProducerTempImages } from '../api';
import ProducerRunViewer from '../components/ProducerRunViewer';
import { KEYWORDS_TOKEN_SEPARATORS, PLATFORM_OPTIONS, normalizeKeywordsArray, normalizeOptionalString, normalizeStringArray, openContentManagement, parseMultiline } from './contentProduce/shared';
import { resolveErrorMessage } from '../utils/errorMessage';

const VIDEO_PROVIDER_OPTIONS = [
  { label: '火山引擎 (seedance)', value: 'seedance' },
  { label: '阿里云万相 (Wan)', value: 'wan' },
  { label: 'Vidu', value: 'vidu' },
];

const VIDEO_ASPECT_RATIO_OPTIONS = [
  { label: '9:16（竖屏）', value: '9:16' },
  { label: '16:9（横屏）', value: '16:9' },
  { label: '3:4（竖构图）', value: '3:4' },
  { label: '4:3（横构图）', value: '4:3' },
  { label: '1:1（方形）', value: '1:1' },
];

const normalizeVideoProvider = (value) => {
  const key = String(normalizeOptionalString(value) || '').toLowerCase();
  if (key === 'wanx') return 'wan';
  if (key === 'volcengine') return 'seedance';
  return key;
};

const parseTabFromSearch = (search) => {
  const tab = new URLSearchParams(search || '').get('tab');
  if (tab === 'papers_to_video') return 'papers_to_video';
  if (tab === 'papers_to_wechat_article') return 'papers_to_wechat_article';
  if (tab === 'xhs_video_importer') return 'xhs_video_importer';
  if (tab === 'weixin_channels_video_importer') return 'weixin_channels_video_importer';
  if (tab === 'video_bilingual_convert') return 'video_bilingual_convert';
  if (tab === 'idea_to_video') return 'idea_to_video';
  return 'papers_to_notes';
};

const ensureProduceTabInSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  if (
    tab !== 'papers_to_notes'
    && tab !== 'papers_to_video'
    && tab !== 'papers_to_wechat_article'
    && tab !== 'xhs_video_importer'
    && tab !== 'weixin_channels_video_importer'
    && tab !== 'video_bilingual_convert'
    && tab !== 'idea_to_video'
  ) {
    params.set('tab', 'papers_to_notes');
  }
  return params.toString();
};

const PapersToNotesForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: normalizeKeywordsArray(values.keywords),
        days: values.days,
        max_count: values.max_count,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        extra_params: {
          max_images: values.max_images,
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('papers_to_notes', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">论文 → 小红书风格图文笔记（写入 content_v1，状态=待发布）</Descriptions.Item>
            <Descriptions.Item label="数据来源">关键词抓取（arXiv）</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              days: 7,
              max_count: 3,
              creator_user_id: 'producer_bot',
              available_platforms: ['小红书'],
              max_images: 6,
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：论文→小红书笔记（日更）" allowClear />
            </Form.Item>

            <Form.Item
              name="keywords"
              label="关键词（用于抓取论文）"
              tooltip="可留空改用 pdf_paths；多个关键词会用于抓取/筛选"
            >
              <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
            </Form.Item>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="days" label="最近 N 天" rules={[{ required: true }]}>
                  <InputNumber min={1} max={365} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="max_count" label="最多处理条数" rules={[{ required: true }]}>
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item name="available_platforms" label="可投递平台">
              <Select
                mode="multiple"
                allowClear
                placeholder="默认 小红书"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item name="max_images" label="最大图片数">
                          <InputNumber min={1} max={50} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="force" label="强制执行">
                          <Radio.Group
                            options={[
                              { label: '否', value: false },
                              { label: '是', value: true },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="papers_to_notes"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const PapersToVideoForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: normalizeKeywordsArray(values.keywords),
        days: values.days,
        max_count: values.max_count,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        pdf_paths: parseMultiline(values.pdf_paths),
        extra_params: {
          scene_count: values.scene_count,
          duration_sec: values.duration_sec,
          aspect_ratio: normalizeOptionalString(values.aspect_ratio) || '9:16',
          output_language: normalizeOptionalString(values.output_language) || 'zh',
          image_size: normalizeOptionalString(values.image_size),
          image_model: normalizeOptionalString(values.image_model),
          image_style: normalizeOptionalString(values.image_style),
          tts_voice: normalizeOptionalString(values.tts_voice),
          upload_images: !!values.upload_images,
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('papers_to_video', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">论文 → 30s 竖屏短视频（含配音/字幕）</Descriptions.Item>
            <Descriptions.Item label="依赖">DashScope 文生图 + TTS（需配置 API Key）</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              days: 7,
              max_count: 3,
              creator_user_id: 'producer_bot',
              available_platforms: ['抖音', '小红书', '快手', '微信视频号'],
              scene_count: 6,
              duration_sec: 30,
              aspect_ratio: '9:16',
              output_language: 'zh',
              image_size: '',
              upload_images: false,
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：论文→短视频（日更）" allowClear />
            </Form.Item>

            <Form.Item
              name="keywords"
              label="关键词（用于抓取论文）"
              rules={[{ required: true, message: '请至少输入 1 个关键词' }]}
              tooltip="多个关键词会用于抓取/筛选；具体抓取逻辑以服务端为准"
            >
              <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
            </Form.Item>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="days" label="最近 N 天" rules={[{ required: true }]}>
                  <InputNumber min={1} max={365} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="max_count" label="最多处理条数" rules={[{ required: true }]}>
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item name="available_platforms" label="可投递平台">
              <Select
                mode="multiple"
                allowClear
                placeholder="默认 抖音/小红书/快手/视频号"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item name="scene_count" label="场景数量">
                          <InputNumber min={3} max={10} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="duration_sec" label="视频时长（秒）">
                          <InputNumber min={10} max={120} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="aspect_ratio" label="视频比例">
                          <Select options={VIDEO_ASPECT_RATIO_OPTIONS} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="output_language" label="输出语言">
                          <Radio.Group
                            options={[
                              { label: '中文', value: 'zh' },
                              { label: 'English', value: 'en' },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="image_size" label="图片尺寸">
                          <Input placeholder="留空按视频比例自动选择（如 16:9 -> 1280*720）" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="image_model" label="图片模型">
                          <Input placeholder="可覆盖 DashScope 模型" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="image_style" label="图片风格">
                          <Input placeholder="统一风格补充（可选）" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="tts_voice" label="配音音色">
                          <Input placeholder="TTS 声音 ID（可选）" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="upload_images" label="上传图片" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="force" label="强制执行">
                          <Radio.Group
                            options={[
                              { label: '否', value: false },
                              { label: '是', value: true },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={24}>
                        <Form.Item name="pdf_paths" label="文档路径（PDF，可选，按行输入本地路径）">
                          <Input.TextArea rows={4} placeholder="/path/to/paper1.pdf" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Button type="link" onClick={openContentManagement}>
                          打开内容库（新窗口）
                        </Button>
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="papers_to_video"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const IdeaToVideoForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');
  const [optimizedPlan, setOptimizedPlan] = useState(null);
  const [optimizedPlanContext, setOptimizedPlanContext] = useState(null);
  const [optimizedTextBaseline, setOptimizedTextBaseline] = useState('');
  const runIdeaContextRef = useRef(null);
  const [referenceFileList, setReferenceFileList] = useState([]);
  const [firstFrameFileList, setFirstFrameFileList] = useState([]);
  const [lastFrameFileList, setLastFrameFileList] = useState([]);

  const optimizeIdea = async () => {
    try {
      const values = await form.validateFields(['idea_text']);
      setOptimizing(true);
      const resp = await optimizeIdeaToVideo({
        idea_text: normalizeOptionalString(values.idea_text),
        style: normalizeOptionalString(form.getFieldValue('style')) || '真实电影感',
        storyboard_count: form.getFieldValue('storyboard_count') || 6,
        duration_sec: form.getFieldValue('duration_sec') || 30,
      });
      if (resp?.status === 'success') {
        const optimizedText = normalizeOptionalString(resp.optimized_idea_text || values.idea_text);
        form.setFieldValue('optimized_idea_text', optimizedText);
        setOptimizedPlan(resp?.plan && typeof resp.plan === 'object' ? resp.plan : null);
        setOptimizedPlanContext({
          idea_text: normalizeOptionalString(values.idea_text),
          style: normalizeOptionalString(form.getFieldValue('style')) || '真实电影感',
          storyboard_count: Number(form.getFieldValue('storyboard_count') || 6),
          duration_sec: Number(form.getFieldValue('duration_sec') || 30),
        });
        setOptimizedTextBaseline(optimizedText);
        message.success('创意优化完成，可继续人工修改');
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '创意优化失败'));
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || String(e));
    } finally {
      setOptimizing(false);
    }
  };

  const handleRunChange = useCallback((runDoc) => {
    const extra = runDoc?.params?.extra_params || {};
    const ideaText = normalizeOptionalString(extra.original_idea_text) || normalizeOptionalString(extra.idea_text);
    if (!ideaText) return;
    const context = {
      idea_text: ideaText,
      style: normalizeOptionalString(extra.style) || '真实电影感',
      storyboard_count: Number(extra.storyboard_count || 6),
      duration_sec: Number(extra.duration_sec || 30),
    };
    runIdeaContextRef.current = context;

    const persisted = runDoc?.idea_optimization || runDoc?.progress?.extra;
    const persistedText = normalizeOptionalString(persisted?.optimized_idea_text);
    const currentOptimized = normalizeOptionalString(form.getFieldValue('optimized_idea_text'));
    if (!persistedText || currentOptimized) return;
    form.setFieldValue('optimized_idea_text', persistedText);
    setOptimizedTextBaseline(persistedText);
    if (persisted?.optimized_plan && typeof persisted.optimized_plan === 'object') {
      setOptimizedPlan(persisted.optimized_plan);
      setOptimizedPlanContext(context);
    }
  }, [form]);

  const handleRunProgressChange = useCallback((progressPayload) => {
    if (!progressPayload || progressPayload.phase !== 'idea') return;
    const extra = progressPayload?.extra;
    if (!extra || typeof extra !== 'object') return;
    const optimizedText = normalizeOptionalString(extra.optimized_idea_text);
    if (!optimizedText) return;
    const currentOptimized = normalizeOptionalString(form.getFieldValue('optimized_idea_text'));
    if (currentOptimized) return;

    form.setFieldValue('optimized_idea_text', optimizedText);
    setOptimizedTextBaseline(optimizedText);

    if (extra.optimized_plan && typeof extra.optimized_plan === 'object') {
      setOptimizedPlan(extra.optimized_plan);
    }
    if (runIdeaContextRef.current) {
      setOptimizedPlanContext(runIdeaContextRef.current);
    }
  }, [form]);

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const textReferenceSources = parseMultiline(values.reference_sources);
      let uploadedReferencePaths = [];
      let uploadedFirstFramePath;
      let uploadedLastFramePath;

      if (referenceFileList.length > 0) {
        const uploadResp = await uploadProducerTempImages(referenceFileList, { category: 'idea_to_video_refs' });
        uploadedReferencePaths = Array.isArray(uploadResp?.items)
          ? uploadResp.items.map((item) => item?.local_path).filter(Boolean)
          : [];
      }

      if (firstFrameFileList.length > 0) {
        const firstResp = await uploadProducerTempImages(firstFrameFileList.slice(0, 1), { category: 'idea_to_video_first' });
        uploadedFirstFramePath = firstResp?.items?.[0]?.local_path;
      }

      if (lastFrameFileList.length > 0) {
        const lastResp = await uploadProducerTempImages(lastFrameFileList.slice(0, 1), { category: 'idea_to_video_last' });
        uploadedLastFramePath = lastResp?.items?.[0]?.local_path;
      }

      const referenceSources = [...textReferenceSources, ...uploadedReferencePaths];
      if (uploadedFirstFramePath) referenceSources.unshift(uploadedFirstFramePath);
      if (uploadedLastFramePath) referenceSources.push(uploadedLastFramePath);
      const originalIdeaText = normalizeOptionalString(values.idea_text);
      const optimizedIdeaText = normalizeOptionalString(values.optimized_idea_text);
      const effectiveIdeaText = optimizedIdeaText || originalIdeaText;
      if (!effectiveIdeaText) {
        message.warning('请先输入创意描述');
        return;
      }
      const currentOptimizeContext = {
        idea_text: originalIdeaText,
        style: normalizeOptionalString(values.style) || '真实电影感',
        storyboard_count: Number(values.storyboard_count || 6),
        duration_sec: Number(values.duration_sec || 30),
      };
      const canReuseOptimizedPlan = !!(
        optimizedPlan
        && optimizedPlanContext
        && optimizedIdeaText
        && optimizedTextBaseline
        && optimizedIdeaText === optimizedTextBaseline
        && currentOptimizeContext.idea_text === optimizedPlanContext.idea_text
        && currentOptimizeContext.style === optimizedPlanContext.style
        && currentOptimizeContext.storyboard_count === optimizedPlanContext.storyboard_count
        && currentOptimizeContext.duration_sec === optimizedPlanContext.duration_sec
      );

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: [],
        days: 1,
        max_count: 1,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        extra_params: {
          idea_text: effectiveIdeaText,
          original_idea_text: originalIdeaText,
          optimized_idea_text: optimizedIdeaText,
          optimized_plan: canReuseOptimizedPlan ? optimizedPlan : undefined,
          video_provider: normalizeVideoProvider(values.video_provider) || 'seedance',
          aspect_ratio: normalizeOptionalString(values.aspect_ratio) || '9:16',
          duration_preset: normalizeOptionalString(values.duration_preset) || '~30s',
          duration_sec: values.duration_sec,
          style: normalizeOptionalString(values.style),
          model_name: normalizeOptionalString(values.model_name),
          storyboard_count: values.storyboard_count,
          include_subtitles: !!values.include_subtitles,
          reference_sources: referenceSources,
          first_frame_source: uploadedFirstFramePath || undefined,
          last_frame_source: uploadedLastFramePath || undefined,
          tts_voice: normalizeOptionalString(values.tts_voice),
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('idea_to_video', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建视频生成任务，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">自然语言创意 → AI优化分镜 → 生成视频（写入 content_v1，状态=待发布）</Descriptions.Item>
            <Descriptions.Item label="说明">支持上传参考图/首尾帧；未配置视频模型时自动走本地兜底渲染</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              creator_user_id: 'producer_bot',
              available_platforms: ['抖音', '小红书', '快手', '微信视频号'],
              aspect_ratio: '9:16',
              duration_preset: '~30s',
              duration_sec: 30,
              video_provider: 'seedance',
              style: '真实电影感',
              storyboard_count: 6,
              include_subtitles: true,
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：创意短视频实验（A/B）" allowClear />
            </Form.Item>

            <Form.Item
              name="idea_text"
              label="创意描述（自然语言）"
              rules={[{ required: true, message: '请输入创意描述' }]}
            >
              <Input.TextArea rows={5} placeholder="例如：做一条 30 秒竖屏视频，主题是“AI 如何帮助科研新人快速读懂论文”，希望前 3 秒抓人，整体风格偏纪实科技感。" />
            </Form.Item>

            <Space style={{ marginBottom: 12 }}>
              <Button onClick={optimizeIdea} loading={optimizing} disabled={!enabled || submitting}>
                AI优化创意
              </Button>
            </Space>

            <Form.Item
              name="optimized_idea_text"
              label="优化后创意（可编辑）"
              tooltip="点击“AI优化创意”后会自动填充；你可以继续人工修改，开始生产时优先使用这里的内容"
            >
              <Input.TextArea rows={7} placeholder="AI优化结果会显示在这里，你可以继续改写镜头、画面、语言与风格。" />
            </Form.Item>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="aspect_ratio" label="比例" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: '9:16（竖屏）', value: '9:16' },
                      { label: '3:4（竖构图）', value: '3:4' },
                      { label: '4:3（横构图）', value: '4:3' },
                      { label: '16:9（横屏）', value: '16:9' },
                      { label: '1:1（方形）', value: '1:1' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="duration_preset" label="时长预设" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: '~15s', value: '~15s' },
                      { label: '~30s', value: '~30s' },
                      { label: '40-60s', value: '40-60s' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="duration_sec" label="目标时长（秒）" rules={[{ required: true }]}>
                  <InputNumber min={8} max={180} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="storyboard_count" label="分镜数量" rules={[{ required: true }]}>
                  <InputNumber min={3} max={12} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="style" label="风格">
              <Select
                allowClear
                showSearch
                placeholder="选择或输入风格"
                options={[
                  { label: '真实电影感', value: '真实电影感' },
                  { label: '纪录片写实', value: '纪录片写实' },
                  { label: '二次元插画', value: '二次元插画' },
                  { label: '极简产品广告', value: '极简产品广告' },
                  { label: '科技未来感', value: '科技未来感' },
                ]}
              />
            </Form.Item>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item name="available_platforms" label="可投递平台">
              <Select
                mode="multiple"
                allowClear
                placeholder="默认 抖音/小红书/快手/视频号"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <>
                      <Form.Item name="reference_sources" label="参考图片来源（每行一个 URL 或服务器路径）">
                        <Input.TextArea rows={4} placeholder={"https://.../ref1.png\n/data/materials/ref2.jpg"} />
                      </Form.Item>
                      <Form.Item label="上传参考图片（多张，可选）">
                        <Upload
                          multiple
                          accept="image/*"
                          beforeUpload={() => false}
                          fileList={referenceFileList}
                          onChange={({ fileList }) => setReferenceFileList(fileList.slice(-8))}
                        >
                          <Button>选择图片</Button>
                        </Upload>
                      </Form.Item>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item label="上传首帧（可选）">
                            <Upload
                              accept="image/*"
                              beforeUpload={() => false}
                              maxCount={1}
                              fileList={firstFrameFileList}
                              onChange={({ fileList }) => setFirstFrameFileList(fileList.slice(-1))}
                            >
                              <Button>选择首帧</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="上传尾帧（可选）">
                            <Upload
                              accept="image/*"
                              beforeUpload={() => false}
                              maxCount={1}
                              fileList={lastFrameFileList}
                              onChange={({ fileList }) => setLastFrameFileList(fileList.slice(-1))}
                            >
                              <Button>选择尾帧</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="video_provider" label="视频 Provider" rules={[{ required: true }]}>
                            <Select options={VIDEO_PROVIDER_OPTIONS} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="model_name" label="视频模型（可选）">
                            <Input placeholder="为空则用后端默认模型" allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="tts_voice" label="配音音色（可选）">
                            <Input placeholder="如 female-tianmei" allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="include_subtitles" label="叠加字幕" valuePropName="checked">
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="force" label="强制执行">
                            <Radio.Group
                              options={[
                                { label: '否', value: false },
                                { label: '是', value: true },
                              ]}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  setOptimizedPlan(null);
                  setOptimizedPlanContext(null);
                  setOptimizedTextBaseline('');
                }}
                disabled={submitting}
              >
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="idea_to_video"
            onStatusChange={setRunStatus}
            onRunChange={handleRunChange}
            onProgressChange={handleRunProgressChange}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const PapersToWechatArticleForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: normalizeKeywordsArray(values.keywords),
        days: values.days,
        max_count: values.max_count,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        extra_params: {
          author: normalizeOptionalString(values.author),
          digest: normalizeOptionalString(values.digest),
          tags: normalizeStringArray(values.tags),
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('papers_to_wechat_article', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">论文 → 微信公众号图文 HTML（写入 content_v1，状态=待发布）</Descriptions.Item>
            <Descriptions.Item label="数据来源">关键词抓取（arXiv）</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              days: 7,
              max_count: 2,
              creator_user_id: 'producer_bot',
              available_platforms: ['微信公众号'],
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：论文→公众号文章（每周）" allowClear />
            </Form.Item>

            <Form.Item
              name="keywords"
              label="关键词（用于抓取论文）"
              rules={[{ required: true, message: '请至少输入 1 个关键词' }]}
            >
              <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
            </Form.Item>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="days" label="最近 N 天" rules={[{ required: true }]}>
                  <InputNumber min={1} max={365} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="max_count" label="最多处理条数" rules={[{ required: true }]}>
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item name="available_platforms" label="可投递平台">
              <Select
                mode="multiple"
                allowClear
                placeholder="默认 微信公众号"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="author" label="作者">
                            <Input placeholder="可选" allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="tags" label="标签（可选）">
                            <Select mode="tags" placeholder="输入标签后回车" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="digest" label="摘要（可选）">
                        <Input.TextArea rows={3} placeholder="摘要/导语" allowClear />
                      </Form.Item>
                      <Form.Item name="force" label="强制执行">
                        <Radio.Group
                          options={[
                            { label: '否', value: false },
                            { label: '是', value: true },
                          ]}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="papers_to_wechat_article"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const XhsVideoImporterForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');

  const mode = Form.useWatch('mode', form);
  const isAccountMode = mode === 'accounts';

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const accounts = parseMultiline(values.accountsText);
      const keywords = normalizeKeywordsArray(values.keywords);
      if (isAccountMode && accounts.length === 0) {
        message.warning('账号模式下请至少输入 1 个账号/主页链接');
        return;
      }
      if (!isAccountMode && keywords.length === 0) {
        message.warning('关键词模式下请至少输入 1 个关键词');
        return;
      }

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: isAccountMode ? [] : keywords,
        days: values.days,
        max_count: values.max_count,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        extra_params: {
          accounts: isAccountMode ? accounts : [],
          headless: !!values.headless,
          login_wait: values.login_wait,
          max_scroll: values.max_scroll,
          scroll_pause: values.scroll_pause,
          note_list_type: values.note_list_type,
          video_only: values.video_only !== false,
          deduplicate_across_keywords: values.deduplicate_across_keywords !== false,
          deduplicate_across_accounts: values.deduplicate_across_accounts !== false,
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('xhs_video_importer', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">小红书抓取 → 视频内容导入（写入 content_v1，状态=待发布）</Descriptions.Item>
            <Descriptions.Item label="提示">首次运行可能需要扫码登录（login_wait/无头模式由高级设置控制）</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              mode: 'accounts',
              days: 1,
              max_count: 10,
              creator_user_id: 'producer_bot',
              available_platforms: [],
              headless: false,
              login_wait: 30,
              max_scroll: 10,
              scroll_pause: 1.5,
              note_list_type: 'published',
              video_only: true,
              deduplicate_across_keywords: true,
              deduplicate_across_accounts: true,
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：小红书视频导入（手动）" allowClear />
            </Form.Item>

            <Form.Item name="mode" label="模式">
              <Radio.Group
                options={[
                  { label: '账号模式', value: 'accounts' },
                  { label: '关键词模式', value: 'keywords' },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            {isAccountMode ? (
              <Form.Item
                name="accountsText"
                label="账号列表（每行一个账号主页 URL 或 ID）"
                rules={[{ required: true, message: '请输入账号主页 URL 或 ID' }]}
              >
                <Input.TextArea rows={4} placeholder="例如：https://www.xiaohongshu.com/user/profile/xxxx" />
              </Form.Item>
            ) : (
              <Form.Item
                name="keywords"
                label="关键词（搜索用）"
                rules={[{ required: true, message: '请至少输入 1 个关键词' }]}
              >
                <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
              </Form.Item>
            )}

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="days" label="最近 N 天" rules={[{ required: true }]}>
                  <InputNumber min={1} max={365} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="max_count" label="最多处理条数" rules={[{ required: true }]}>
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item
              name="available_platforms"
              label="可投递平台（可留空走后端默认）"
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="留空则使用后端默认平台集合"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="headless" label="无头模式">
                            <Radio.Group
                              options={[
                                { label: '否（可扫码）', value: false },
                                { label: '是（无头）', value: true },
                              ]}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="login_wait" label="登录等待（秒）">
                            <InputNumber min={0} max={600} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="max_scroll" label="最大滚动次数">
                            <InputNumber min={0} max={200} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="scroll_pause" label="滚动间隔（秒）">
                            <InputNumber min={0} max={30} step={0.1} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="note_list_type" label="笔记列表类型">
                            <Select
                              options={[
                                { label: '已发布', value: 'published' },
                                { label: '点赞', value: 'liked' },
                                { label: '收藏', value: 'collected' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="video_only" label="仅视频">
                            <Select
                              options={[
                                { label: '是', value: true },
                                { label: '否', value: false },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="deduplicate_across_accounts" label="跨账号去重">
                            <Select
                              options={[
                                { label: '是', value: true },
                                { label: '否', value: false },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="deduplicate_across_keywords" label="跨关键词去重">
                            <Select
                              options={[
                                { label: '是', value: true },
                                { label: '否', value: false },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item name="force" label="强制执行">
                        <Radio.Group
                          options={[
                            { label: '否', value: false },
                            { label: '是', value: true },
                          ]}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="xhs_video_importer"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const WeixinChannelsVideoImporterForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        keywords: [],
        days: 1,
        max_count: 1,
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        available_platforms: normalizeStringArray(values.available_platforms),
        extra_params: {
          proxy_port: values.proxy_port,
          command_timeout_sec: values.command_timeout_sec,
          download_watch_dir: normalizeOptionalString(values.download_watch_dir),
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('weixin_channels_video_importer', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">视频号（微信客户端）→ 视频导入（写入 content_v1，状态=待发布）</Descriptions.Item>
            <Descriptions.Item label="提示">运行时启动本机代理，并监听下载目录新增视频文件</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              creator_user_id: 'producer_bot',
              available_platforms: ['微信视频号', '抖音', '快手', '小红书', '百家号'],
              proxy_port: 2023,
              command_timeout_sec: 600,
              download_watch_dir: '~/Downloads/weixin_channels',
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：视频号视频导入" allowClear />
            </Form.Item>
            <Alert
              type="info"
              showIcon
              message="操作步骤"
              description="1) 点击“开始生产”启动代理；2) 在微信客户端打开目标视频详情页；3) 点击“下载”并保存到监控目录（默认 ~/Downloads/weixin_channels，建议空目录）。"
              style={{ marginBottom: 12 }}
            />

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Form.Item
              name="available_platforms"
              label="可投递平台"
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="默认 微信视频号/抖音/快手/小红书/百家号"
                options={PLATFORM_OPTIONS}
              />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <>
                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="proxy_port" label="代理端口">
                            <InputNumber min={1000} max={65535} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="download_watch_dir" label="下载监听目录">
                            <Input placeholder="例如：~/Downloads/weixin_channels" allowClear />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item name="command_timeout_sec" label="下载等待超时（秒）">
                            <InputNumber min={30} max={3600} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item name="force" label="强制执行">
                        <Radio.Group
                          options={[
                            { label: '否', value: false },
                            { label: '是', value: true },
                          ]}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="weixin_channels_video_importer"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const VideoBilingualConvertForm = ({ enabled }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [runId, setRunId] = useState('');
  const [runStatus, setRunStatus] = useState('');
  const [searching, setSearching] = useState(false);
  const [contentOptions, setContentOptions] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const searchTimerRef = useRef(null);
  const contentMapRef = useRef(new Map());

  const contentId = Form.useWatch('content_id', form);

  useEffect(() => {
    if (!contentId) {
      setSelectedContent(null);
      return;
    }
    const hit = contentMapRef.current.get(contentId);
    setSelectedContent(hit || null);
  }, [contentId]);

  useEffect(() => () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
  }, []);

  const handleSearchContent = (value) => {
    const keyword = String(value || '').trim();
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    if (!keyword) {
      setContentOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await searchContent({
          page: 1,
          page_size: 20,
          keyword,
          filters: { content_type: ['视频'] },
          sort: { field: 'ingested_at', order: 'desc' },
        });
        if (resp?.status === 'success') {
          const items = Array.isArray(resp?.items) ? resp.items : [];
          const map = new Map();
          const opts = items
            .map((item) => {
              const id = String(item?.content_id || '').trim();
              if (!id) return null;
              map.set(id, item);
              const title = String(item?.title || item?.summary || '').trim();
              const label = title ? `${title} (${id})` : id;
              return { value: id, label };
            })
            .filter(Boolean);
          contentMapRef.current = map;
          setContentOptions(opts);
        } else {
          setContentOptions([]);
        }
      } catch (e) {
        setContentOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelectContent = (value) => {
    const id = String(value || '').trim();
    form.setFieldsValue({ content_id: id || undefined });
    const hit = contentMapRef.current.get(id);
    setSelectedContent(hit || null);
  };

  const runOnce = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setCreateError('');

      const payload = {
        task_name: normalizeOptionalString(values.task_name),
        creator_user_id: normalizeOptionalString(values.creator_user_id),
        extra_params: {
          content_id: values.content_id,
          source_lang: normalizeOptionalString(values.source_lang) || 'auto',
          target_lang: normalizeOptionalString(values.target_lang),
          subtitle_mode: normalizeOptionalString(values.subtitle_mode) || 'soft',
          tts_voice: normalizeOptionalString(values.tts_voice),
          bgm_volume: values.bgm_volume,
          tts_volume: values.tts_volume,
          max_tts_rate: values.max_tts_rate,
          subtitle_cover: values.subtitle_cover,
          subtitle_cover_ratio: values.subtitle_cover_ratio,
          subtitle_cover_height: values.subtitle_cover_height,
          subtitle_cover_color: normalizeOptionalString(values.subtitle_cover_color),
          force: !!values.force,
        },
      };

      const resp = await createProducerRun('video_bilingual_convert', payload);
      if (resp?.status === 'accepted' && resp.run_id) {
        setRunId(resp.run_id);
        setRunStatus(resp.run_status || 'queued');
        message.success('已创建生产运行，正在执行…');
      } else {
        const msg = resolveErrorMessage(resp?.error_message, '创建运行失败');
        setCreateError(msg);
        message.error(msg);
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.message || String(e);
      setCreateError(msg);
      message.error('创建运行失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={12}>
      <Col xs={24} lg={14}>
        <Card
          title="参数配置"
          size="small"
          extra={enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">未注册</Tag>}
        >
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="用途">已有视频 → 保留背景音 + 目标语言配音 + 字幕</Descriptions.Item>
            <Descriptions.Item label="数据来源">内容库（content_v1）</Descriptions.Item>
          </Descriptions>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              creator_user_id: 'producer_bot',
              source_lang: 'auto',
              target_lang: 'zh',
              subtitle_mode: 'burned',
              tts_voice: '',
              bgm_volume: 0.7,
              tts_volume: 1.0,
              max_tts_rate: 1.25,
              subtitle_cover: true,
              subtitle_cover_ratio: 0.22,
              subtitle_cover_color: 'black@1.0',
              subtitle_cover_height: 160,
              force: false,
            }}
          >
            <Form.Item name="task_name" label="任务名称（可选）">
              <Input placeholder="例如：视频双语转换（英文→中文）" allowClear />
            </Form.Item>

            <Form.Item label="搜索内容（视频）">
              <Select
                showSearch
                allowClear
                placeholder="输入标题/关键词检索内容"
                filterOption={false}
                options={contentOptions}
                onSearch={handleSearchContent}
                onSelect={handleSelectContent}
                onChange={(value) => {
                  if (!value) {
                    form.setFieldsValue({ content_id: undefined });
                    setSelectedContent(null);
                  }
                }}
                notFoundContent={searching ? <Spin size="small" /> : '暂无结果'}
              />
            </Form.Item>

            <Form.Item
              name="content_id"
              label="内容ID（必填）"
              rules={[{ required: true, message: '请选择或输入 content_id' }]}
            >
              <Input placeholder="也可手动粘贴 content_id" allowClear />
            </Form.Item>

            {selectedContent && (
              <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
                <Descriptions.Item label="标题">{selectedContent?.title || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">{selectedContent?.status || '-'}</Descriptions.Item>
                <Descriptions.Item label="内容类型">{selectedContent?.content_type || '-'}</Descriptions.Item>
              </Descriptions>
            )}

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="source_lang" label="源语言（auto/zh/en/...）">
                  <Input placeholder="auto / zh / en / ja ..." allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="target_lang" label="目标语言（必填）" rules={[{ required: true }]}>
                  <Input placeholder="zh / en / ja ..." allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="subtitle_mode" label="字幕模式">
                  <Select
                    options={[
                      { label: '软字幕', value: 'soft' },
                      { label: '烧录字幕', value: 'burned' },
                      { label: '软字幕 + 上传', value: 'both' },
                      { label: '不生成字幕', value: 'none' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="tts_voice" label="配音音色（可选）">
                  <Input placeholder="默认使用服务端 voice_id" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item name="bgm_volume" label="背景音乐音量">
                  <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="tts_volume" label="配音音量">
                  <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="max_tts_rate" label="语速上限">
                  <InputNumber min={1} max={2} step={0.05} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="creator_user_id" label="创建者ID">
              <Input placeholder="默认 producer_bot" allowClear />
            </Form.Item>

            <Collapse
              items={[
                {
                  key: 'advanced',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      高级设置
                    </Space>
                  ),
                  children: (
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item name="subtitle_cover" label="遮盖原字幕区域">
                          <Radio.Group
                            options={[
                              { label: '是', value: true },
                              { label: '否', value: false },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="subtitle_cover_ratio" label="遮盖比例（高度占比）">
                          <InputNumber min={0.05} max={0.5} step={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="subtitle_cover_height" label="遮盖高度（px）">
                          <InputNumber min={24} max={600} step={4} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="subtitle_cover_color" label="遮盖颜色">
                          <Input placeholder="black@1.0" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="force" label="强制执行">
                          <Radio.Group
                            options={[
                              { label: '否', value: false },
                              { label: '是', value: true },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Button type="link" onClick={openContentManagement}>
                          打开内容库（新窗口）
                        </Button>
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />

            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={runOnce}
                loading={submitting}
                disabled={!enabled || submitting || runStatus === 'queued' || runStatus === 'running' || runStatus === 'cancel_requested'}
              >
                开始生产
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message="创建运行失败" description={createError} />}
          <ProducerRunViewer
            runId={runId}
            producerName="video_bilingual_convert"
            onStatusChange={setRunStatus}
            title="运行进度"
          />
        </Space>
      </Col>
    </Row>
  );
};

const ContentProduce = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registry, setRegistry] = useState({});
  const [registryError, setRegistryError] = useState('');
  const activeTab = useMemo(() => parseTabFromSearch(location.search), [location.search]);
  const [mountedTabs, setMountedTabs] = useState(() => ({
    papers_to_notes: activeTab === 'papers_to_notes',
    papers_to_video: activeTab === 'papers_to_video',
    idea_to_video: activeTab === 'idea_to_video',
    papers_to_wechat_article: activeTab === 'papers_to_wechat_article',
    xhs_video_importer: activeTab === 'xhs_video_importer',
    weixin_channels_video_importer: activeTab === 'weixin_channels_video_importer',
    video_bilingual_convert: activeTab === 'video_bilingual_convert',
  }));

  // 保证 URL 始终携带 tab，便于刷新/分享链接
  useEffect(() => {
    const normalized = ensureProduceTabInSearch(location.search);
    const current = (location.search || '').replace(/^\?/, '');
    if (normalized !== current) {
      navigate({ pathname: location.pathname, search: normalized }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const refreshRegistry = useCallback(async () => {
    setRegistryLoading(true);
    setRegistryError('');
    try {
      const resp = await getProducersRegistry();
      if (resp?.status === 'success') {
        setRegistry(resp.items || {});
      } else {
        setRegistryError(resolveErrorMessage(resp?.error_message, '获取 producers 列表失败'));
      }
    } catch (e) {
      setRegistryError(e?.message || String(e));
    } finally {
      setRegistryLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRegistry();
  }, [refreshRegistry]);

  useEffect(() => {
    setMountedTabs((prev) => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);

  const enabledSet = useMemo(() => new Set(Object.keys(registry || {})), [registry]);
  const availabilityKnown = enabledSet.size > 0 && !registryError;
  const isEnabled = useCallback((name) => (availabilityKnown ? enabledSet.has(name) : true), [availabilityKnown, enabledSet]);
  const show = (name) => (activeTab === name ? 'block' : 'none');

  return (
    <div className="page-shell">
      <Card className="page-card" bodyStyle={{ padding: 0 }}>
        <div className="page-panel">
          <div className="page-panel__body">
            <Spin spinning={registryLoading} tip="加载能力中…">
              {registryError && (
                <Alert
                  type="warning"
                  showIcon
                  message="无法获取 producers 列表，将按默认能力显示"
                  description={registryError}
                  style={{ margin: '12px 12px 0' }}
                />
              )}
              {mountedTabs.papers_to_notes && (
                <div style={{ display: show('papers_to_notes') }}>
                  <PapersToNotesForm enabled={isEnabled('papers_to_notes')} />
                </div>
              )}
              {mountedTabs.papers_to_video && (
                <div style={{ display: show('papers_to_video') }}>
                  <PapersToVideoForm enabled={isEnabled('papers_to_video')} />
                </div>
              )}
              {mountedTabs.idea_to_video && (
                <div style={{ display: show('idea_to_video') }}>
                  <IdeaToVideoForm enabled={isEnabled('idea_to_video')} />
                </div>
              )}
              {mountedTabs.papers_to_wechat_article && (
                <div style={{ display: show('papers_to_wechat_article') }}>
                  <PapersToWechatArticleForm enabled={isEnabled('papers_to_wechat_article')} />
                </div>
              )}
              {mountedTabs.xhs_video_importer && (
                <div style={{ display: show('xhs_video_importer') }}>
                  <XhsVideoImporterForm enabled={isEnabled('xhs_video_importer')} />
                </div>
              )}
              {mountedTabs.weixin_channels_video_importer && (
                <div style={{ display: show('weixin_channels_video_importer') }}>
                  <WeixinChannelsVideoImporterForm enabled={isEnabled('weixin_channels_video_importer')} />
                </div>
              )}
              {mountedTabs.video_bilingual_convert && (
                <div style={{ display: show('video_bilingual_convert') }}>
                  <VideoBilingualConvertForm enabled={isEnabled('video_bilingual_convert')} />
                </div>
              )}
            </Spin>
          </div>
        </div>
      </Card>

    </div>
  );
};

export default ContentProduce;
